import { useApolloClient } from '@apollo/client';
import { Button, Form, message, Space } from 'antd';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouterSpecsFragment } from '../../../api/fragments/RouterSpecs';
import DrawerWithAutoWidth from '../../../components/DrawerWithAutoWidth';
import useHandleError from '../../../utilities/useHandleError';
import { getFormInitialValues, renderFormForEndpointType } from './endpoints';
import * as implementations from './endpoints/implementations';

export type EndpointUpdateFormProps = {
    router: RouterSpecsFragment;
    endpoint: RouterSpecsFragment['endpoints'][number];
    visible: boolean;
    onClose: () => void;
};

const EndpointUpdateForm = ({ router, endpoint, visible, onClose }: EndpointUpdateFormProps) => {
    const apolloClient = useApolloClient();
    const initialValues = useMemo(() => getFormInitialValues(endpoint), [endpoint]);
    const { t } = useTranslation(['routerDetails']);

    const routerId = router.id;
    const endpointId = endpoint.id;
    const endpointType = endpoint.__typename;

    const onSubmit = useHandleError(
        async values => {
            message.loading({
                content: t('routerDetails:endpoints.updateDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            // update the endpoint
            await implementations[endpointType]
                .submitUpdate(apolloClient, routerId, endpointId, values as any)
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about the success
            message.success({
                content: t('routerDetails:endpoints.updateDrawer.messages.success'),
                key: 'primary',
            });

            // then close the drawer
            onClose();
        },
        [apolloClient, routerId, endpointType, endpointId, onClose, t]
    );

    const formName = `endpoint-${endpoint}`;

    return (
        <DrawerWithAutoWidth
            extra={
                <Space>
                    <Button onClick={onClose}>{t('routerDetails:endpoints.updateDrawer.cancelButton')}</Button>
                    <Button form={formName} htmlType="submit" type="primary">
                        {t('routerDetails:endpoints.updateDrawer.submitButton')}
                    </Button>
                </Space>
            }
            onClose={onClose}
            visible={visible}
            destroyOnClose
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                {({ handleSubmit }) => (
                    <Form id={formName} layout="vertical" name={formName} onSubmitCapture={handleSubmit}>
                        {renderFormForEndpointType(endpoint, router)}
                    </Form>
                )}
            </Formik>
        </DrawerWithAutoWidth>
    );
};

export default EndpointUpdateForm;

export const useEndpointUpdateForm = () => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: (props: Pick<EndpointUpdateFormProps, 'router' | 'endpoint'>) => (
            <EndpointUpdateForm {...props} onClose={actions.close} visible={visible} />
        ),
    };
};
